import { useQuery } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getResults, type CandidateHappening } from '@/lib/selections-api';

interface ResultsTableProps {
  selectionId: string;
  roundId: string;
  candidates: CandidateHappening[];
  /** Top-N are highlighted as the consensus picks. */
  targetFinal: number;
}

export function ResultsTable({ selectionId, roundId, candidates, targetFinal }: ResultsTableProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['selection-results', roundId],
    queryFn: () => getResults(selectionId, roundId),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Tallying…</p>;
  if (!data || data.rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No allocations to tally.</p>;
  }

  const title = (hid: string) => candidates.find((c) => c.id === hid)?.title ?? hid;
  const artists = (hid: string) =>
    candidates.find((c) => c.id === hid)?.artists.map((a) => a.name).join(', ') || '—';
  // Each submitted ballot's shares sum to 1, so total score == ballotCount.
  const totalScore = data.ballotCount || 1;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        Results · {data.ballotCount} ballots · top {targetFinal} = consensus picks
      </p>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>Show</TableHead>
              <TableHead>Artists</TableHead>
              <TableHead className="w-24">Share</TableHead>
              <TableHead className="w-20">Points</TableHead>
              <TableHead className="w-16">Voters</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((r, i) => {
              const consensus = i < targetFinal;
              return (
                <TableRow key={r.happening_id} className={consensus ? 'bg-primary/5' : ''}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">
                    {title(r.happening_id)}
                    {consensus && (
                      <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">
                        consensus
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{artists(r.happening_id)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {((r.score / totalScore) * 100).toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-muted-foreground">{r.raw_points}</TableCell>
                  <TableCell className="text-muted-foreground">{r.voter_count}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">
        Ranked by normalized share (each voter's points ÷ their total, summed). Raw points shown for
        legibility.
      </p>
    </div>
  );
}
